/*
 * File: section-tabs.ts
 * Created: 2025-12-09 16:28:17
 * Author: Val Liu <valuis0429@gmail.com>
 * -----
 * Last Modified: 2025-12-10 17:44:47
 * Modified By: Val Liu <valuis0429@gmail.com>
 */

import Vue, { CreateElement, VNode } from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { BaseComponent } from "../base-classes";

type TabMeta = {
    vnode: VNode;
    label: string;
    badge: string | number | null;
    index: number;
};

@Component
export default class SectionsTabs extends BaseComponent {
    /**
     * v-model 對應的 index
     */
    @Prop({ type: Number, default: 0 })
    declare readonly value: number;

    /**
     * 捲動行為：'auto' / 'smooth'
     */
    @Prop({ type: String, default: "smooth" })
    readonly scrollBehavior!: ScrollBehavior;

    /**
     * 若有 fixed header，需要往上補償的 offset（px）
     */
    @Prop({ type: Number, default: 0 })
    readonly scrollOffset!: number;

    activeIndex: number = 0;

    @Watch("value")
    onValueChanged(val: number) {
        this.activeIndex = val;
    }

    @Watch("activeIndex")
    onActiveIndexChanged(val: number) {
        this.$emit("input", val);
        this.$emit("change", val);
    }

    /**
     * 解析 slot 子元件的 label / badge
     */
    tabs(): TabMeta[] {
        const nodes = (this.$slots.default || []).filter(Boolean);

        return nodes.map((vnode: VNode, index: number) => {
            let label = `Section ${index + 1}`;
            let badge: string | number | null = null;

            // 子元件 props
            if (vnode.componentOptions) {
                const propsData = (vnode.componentOptions.propsData as any) || {};
                const dataAttrs = (vnode.data && vnode.data.attrs) || {};

                label = propsData.label || dataAttrs.label || label;

                if (
                    Object.prototype.hasOwnProperty.call(propsData, "badge") ||
                    Object.prototype.hasOwnProperty.call(dataAttrs, "badge")
                ) {
                    badge = propsData.badge != null ? propsData.badge : dataAttrs.badge;
                }
            } else if (vnode.data && vnode.data.attrs) {
                // 允許直接在 DOM attrs 上提供 label
                const attrs: any = vnode.data.attrs;
                label = attrs["data-label"] || attrs.label || label;
            }

            return { vnode, label, badge, index };
        });
    }

    onTabClick(index: number) {
        this.activeIndex = index;

        this.$nextTick(() => {
            const refName = `panel-${index}`;
            let el: any = (this.$refs as any)[refName];

            if (Array.isArray(el)) el = el[0];
            if (!el || typeof el.scrollIntoView !== "function") return;

            el.scrollIntoView({
                behavior: this.scrollBehavior,
                block: "start",
            });

            if (this.scrollOffset) {
                const currentTop =
                    window.pageYOffset ||
                    document.documentElement.scrollTop ||
                    document.body.scrollTop ||
                    0;

                window.scrollTo({
                    top: Math.max(0, currentTop - this.scrollOffset),
                    behavior: this.scrollBehavior,
                });
            }

            // 可選：讓鍵盤/無障礙焦點更完整
            if (typeof el.focus === "function") {
                el.focus({ preventScroll: true });
            }
        });
    }

    render(h: CreateElement): VNode {
        const tabs = this.tabs();

        const nav = h("div", { staticClass: "page-tabs mt-4" }, [
            h(
                "ul",
                { staticClass: "nav nav-tabs" },
                tabs.map((tab) =>
                    h(
                        "li",
                        {
                            staticClass: "nav-item",
                            key: tab.index,
                        },
                        [
                            h(
                                "a",
                                {
                                    staticClass: "nav-link",
                                    class: {
                                        active: this.activeIndex === tab.index,
                                    },
                                    attrs: {
                                        href: "javascript:void(0)",
                                    },
                                    on: {
                                        click: () => this.onTabClick(tab.index),
                                    },
                                },
                                [
                                    tab.label,
                                    tab.badge != null
                                        ? h(
                                              "span",
                                              {
                                                  staticClass:
                                                      "badge badge-light border ml-1",
                                              },
                                              [String(tab.badge)],
                                          )
                                        : null,
                                ],
                            ),
                        ],
                    ),
                ),
            ),
        ]);

        const panels = h(
            "div",
            { staticClass: "page-content mt-3" },
            tabs.map((tab) =>
                h(
                    "div",
                    {
                        key: `panel-${tab.index}`,
                        ref: `panel-${tab.index}`,
                        staticClass: "iv-labeled-sections__panel",
                        attrs: {
                            // 讓 focus 可用（無障礙/鍵盤友善）
                            tabindex: "-1",
                            "data-section-index": String(tab.index),
                        },
                    },
                    [tab.vnode],
                ),
            ),
        );

        return h("div", { staticClass: "iv-labeled-sections" }, [nav, panels]);
    }
}
